import { AUTH_ROUTES, getMeOutput, } from "@repo/contract/iam";
import { deleteAccount } from "../../api/delete-account";
import { getJson, sendNoContent, } from "../core/request";
import { meKeys } from "./keys";
/**
 * `GET /auth/me` — who the token says this is.
 *
 * Decoded with `getMeOutput`, a `z.object`. `displayName` and `locale` (`D40`)
 * are OPTIONAL there: the server sends both (as `null` when unset), and an
 * older server that sends neither still decodes. Nothing here may require
 * them — read `me.displayName ?? null`.
 */
export function fetchMe(api, init) {
    return getJson(api, AUTH_ROUTES.me, getMeOutput, init);
}
/**
 * `PUT /auth/me/locale` (`D93`) — the language the server writes pushes in.
 * `204`, idempotent, so a retry after a lost response is free.
 */
export function setLocale(api, input) {
    return sendNoContent(api, "PUT", AUTH_ROUTES.setLocale, input);
}
/**
 * `PUT /auth/me/display-name` (`D40`) — what the app calls this person.
 * `null` clears it. `204`, idempotent. The body is not pre-validated here:
 * a blank or over-long name comes back as the server's 422 `ApiError`.
 */
export function setDisplayName(api, input) {
    return sendNoContent(api, "PUT", AUTH_ROUTES.setDisplayName, input);
}
/**
 * `PUT /auth/me/time-zone` (`D87`) — the IANA zone the device is in, e.g.
 * `"America/Sao_Paulo"`. `204`, idempotent, which matters here rather than
 * being a nicety: the app fires this on launch and on resume and forgets it.
 *
 * ── A ZONE NAME, NEVER AN OFFSET ────────────────────────────────────────────
 * The server refuses `"-03:00"`. An offset is DST-blind — the value `D87`
 * rejected — and the whole point of storing a zone is that the server can ask
 * what the clock says on a date it has not reached yet. Send exactly what
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` gave, untouched.
 *
 * Not pre-validated here: the vocabulary is the tz database the RUNTIME
 * ships, so a zone this package refused would be a zone the server can store
 * perfectly well. A bad one comes back as the server's 422 `ApiError`.
 */
export function setTimeZone(api, input) {
    return sendNoContent(api, "PUT", AUTH_ROUTES.setTimeZone, input);
}
/**
 * `PUT /auth/me/weight-unit` (`D266`) — `kg` or `lb`, the unit this person
 * reads loads in. A display preference: every load on every other route
 * stays in kilograms. `204`, idempotent, so the outbox may replay it.
 */
export function setWeightUnit(api, input) {
    return sendNoContent(api, "PUT", AUTH_ROUTES.setWeightUnit, input);
}
export function meOptions(api) {
    return {
        queryFn: ({ signal }) => fetchMe(api, { signal }),
        queryKey: meKeys.account,
    };
}
export function setLocaleMutation(api) {
    return {
        invalidates: [meKeys.all],
        mutationFn: (input) => setLocale(api, input),
        mutationKey: ["me", "locale", "put"],
    };
}
/** Invalidates `["me"]`, so the greeting and the account row refetch. */
export function setDisplayNameMutation(api) {
    return {
        invalidates: [meKeys.all],
        mutationFn: (input) => setDisplayName(api, input),
        mutationKey: ["me", "display-name", "put"],
    };
}
/**
 * `DELETE /auth/account` (`D153`), as a mutation over the ONE existing
 * implementation, `@repo/client/api`'s `deleteAccount` — never a second copy.
 *
 * ── THE LOCAL CLEAR IS THE CALLER'S ─────────────────────────────────────────
 * On success the server has revoked the whole refresh family. The app then
 * calls `ApiClient.discardSession()` — NOT `signOut()`, which would present
 * the dead credential (`delete-account.ts` says why). Nothing is invalidated:
 * the session ending clears the cache.
 *
 * ── THE REFUSAL PROPAGATES ──────────────────────────────────────────────────
 * `409 account.owns_populated_groups` with `params { groupCount, groupNames,
 * memberCount }` arrives as the server's `ApiError`, untouched; the screen
 * renders it.
 */
export function deleteAccountMutation(api) {
    return {
        mutationFn: () => deleteAccount((path, init) => api.authorizedFetch(path, init)),
        mutationKey: ["me", "account", "delete"],
    };
}
/**
 * Sign-out stays `ApiClient.signOut()`: it owns the authenticated goodbye and
 * the local clear, and it never rejects. This is only its mutation shape, so
 * a screen gets `isPending` for the button like every other write.
 */
export function signOutMutation(client) {
    return {
        mutationFn: () => client.signOut(),
        mutationKey: ["me", "sign-out"],
    };
}
//# sourceMappingURL=account.js.map